import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useQuery from '@hooks/useQuery';
import AuthService from '@api/AuthService';
import Loader from '@components/Loader';
import routes from '@constants/routes';
import { Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { UserActions, UserSelectors } from '@store/slices/user';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { clearLocalAuthState, getAuthErrorMessage } from '@utils/authSession';

function getHashParams() {
  return new URLSearchParams(window.location.hash.replace(/^#/, ''));
}

function Login() {
  const query = useQuery();
  const hashParams = useMemo(() => getHashParams(), []);
  const dispatch = useDispatch();
  const isAuthorized = useSelector(UserSelectors.isAuthorized);
  const errorMessage = query.get('errorMessage');
  const authCode = hashParams.get('authCode');
  const hasUnsafeTokenParams = Boolean(
    query.get('accessToken') ||
    query.get('refreshToken'),
  );
  const [exchangeError, setExchangeError] = useState<string | null>(null);
  const exchangedAuthCodeRef = useRef<string | null>(null);
  const authError = errorMessage || exchangeError;

  const authWithAzure = useCallback(() => {
    if (!authCode && !authError && !hasUnsafeTokenParams) {
      window.location.replace(
        AuthService.links.authWithAzure(window.location.origin),
      );
    }
  }, [authCode, authError, hasUnsafeTokenParams]);

  const retryAuth = useCallback(() => {
    clearLocalAuthState();
    window.location.replace(
      AuthService.links.authWithAzure(window.location.origin),
    );
  }, []);

  useEffect(() => {
    authWithAzure();
  }, [authWithAzure]);

  useEffect(() => {
    if (hasUnsafeTokenParams) {
      clearLocalAuthState();
      window.history.replaceState(null, document.title, routes.loginPage);
      setExchangeError('unsafeTokenRedirect');
      return;
    }

    if (errorMessage) {
      clearLocalAuthState();
      return;
    }

    if (!authCode) {
      return;
    }

    if (exchangedAuthCodeRef.current === authCode) {
      return;
    }

    exchangedAuthCodeRef.current = authCode;

    async function exchangeAuthCode() {
      try {
        clearLocalAuthState();

        const { data } = await AuthService.exchangeAzureAuthCode(authCode as string);
        const { accessToken, refreshToken } = data;

        localStorage.setItem('accessToken', accessToken as string);
        localStorage.setItem('refreshToken', refreshToken as string);
        window.history.replaceState(null, document.title, routes.loginPage);

        dispatch(UserActions.setIsAuthorized(true));
      } catch (_error) {
        clearLocalAuthState();
        window.history.replaceState(null, document.title, routes.loginPage);
        setExchangeError('exchangeFailed');
      }
    }

    exchangeAuthCode();
  }, [authCode, dispatch, errorMessage, hasUnsafeTokenParams]);

  const authErrorMessage = useMemo(
    () => getAuthErrorMessage(authError),
    [authError],
  );

  if (authError) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        justifyContent="center"
        alignItems="center"
        px={2}
      >
        <Paper sx={{ p: 4, width: '100%', maxWidth: 460 }}>
          <Stack spacing={3}>
            <Typography variant="h5" component="h1">
              Sign in
            </Typography>
            <Alert severity="error">
              {authErrorMessage}
            </Alert>
            <Button variant="contained" onClick={retryAuth}>
              Sign in again
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (isAuthorized) {
    return <Navigate to={routes.dashboard} />;
  }

  return <Loader />;
}

export default Login;
